import { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import {
  AssignmentWithCourses,
  AssignmentFilterStatus,
  AssignmentStatsData,
} from '../types'
import { getAssignmentStatus, isWithinDateRange } from '../utils/helpers'

export default function useAssignmentFilters(
  assignments: AssignmentWithCourses[],
) {
  // Default: 6 months back, 1 year forward
  let defaultLower = dayjs().subtract(6, 'months').format('YYYY-MM-DD')
  let defaultUpper = dayjs().add(1, 'year').format('YYYY-MM-DD')

  let [statusFilter, setStatusFilter] = useState<AssignmentFilterStatus>('All')
  let [dateLowerFilter, setDateLowerFilter] = useState<string | null>(
    defaultLower,
  )
  let [dateUpperFilter, setDateUpperFilter] = useState<string | null>(
    defaultUpper,
  )

  let filteredAssignments = useMemo(
    () =>
      assignments.filter((assignment) => {
        // Status filter
        let assignmentStatus = getAssignmentStatus(assignment)
        if (statusFilter !== 'All' && assignmentStatus !== statusFilter) {
          return false
        }

        // Date range filter (filter by due date)
        if (assignment.dueDate) {
          if (
            !isWithinDateRange(
              assignment.dueDate,
              dateLowerFilter,
              dateUpperFilter,
            )
          ) {
            return false
          }
        }

        return true
      }),
    [assignments, statusFilter, dateLowerFilter, dateUpperFilter],
  )

  let stats: AssignmentStatsData = useMemo(
    () =>
      filteredAssignments.reduce(
        (acc, assignment) => {
          let status = getAssignmentStatus(assignment)
          if (status === 'Active') {
            return { ...acc, active: acc.active + 1 }
          }
          if (status === 'Completed') {
            return { ...acc, completed: acc.completed + 1 }
          }
          if (status === 'Overdue') {
            return { ...acc, overdue: acc.overdue + 1 }
          }
          return acc
        },
        { active: 0, completed: 0, overdue: 0 },
      ),
    [filteredAssignments],
  )

  return {
    filters: {
      statusFilter,
      dateLowerFilter,
      dateUpperFilter,
    },
    actions: {
      setStatusFilter,
      setDateLowerFilter,
      setDateUpperFilter,
    },
    filteredAssignments,
    stats,
  }
}
